import { useClient, useMutation, useQuery } from '@weenat/client'
import useCreatePlotFromDevice from '@weenat/client/dist/core/devices/useCreatePlotFromDevice'
import ErrCode from '@weenat/client/dist/ErrCode'
import { PossibleError } from '@weenat/client/dist/errors'
import { Device } from '@weenat/client/dist/resources/devices'
import { GetManyPlotsQueryParams, Plot } from '@weenat/client/dist/resources/plots'
import { useIntl } from '@weenat/wintl'
import { useOrgContext } from 'app/orgProvider'
import { useNavigate } from 'app/routx-router'
import logEvent from 'app/utils/analytics'
import { isEmpty, isNil } from 'lodash-es'
import { FC, useCallback, useState } from 'react'
import LinkablePlotItem from '../administration/devices/LinkablePlotItem'
import LinkedPlotLinkItem from '../administration/devices/LinkedPlotLinkItem'
import useDisclosure from '../hooks/useDisclosure'
import useToasts from '../hooks/useToasts'
import AdvancedClientList from '../kit/AdvancedClientList'
import Button from '../kit/Button'
import DelimitedFlex from '../kit/DelimitedFlex'
import Ripple from '../kit/empty-state/Ripple'
import Icons from '../kit/Icons'
import ListEmpty from '../kit/ListEmpty'
import Modal from '../kit/Modal'
import Text from '../kit/Text'

const EMPTY_PLOTS: Plot[] = []

const plotListQueryParams: GetManyPlotsQueryParams = { limit: 10, isArchived: false }

interface IrrigationDeviceDashboardEmptyStateProps {
  device: Device
}

const IrrigationDeviceDashboardEmptyState: FC<IrrigationDeviceDashboardEmptyStateProps> = ({
  device
}) => {
  const org = useOrgContext()
  const { t } = useIntl()
  const client = useClient()
  const nav = useNavigate()
  const { addSuccessToast, addErrorToast } = useToasts()

  const [selectedPlots, setSelectedPlots] = useState<number[]>([])

  const linkPlotsModal = useDisclosure()

  const isLinkedToPlots = !isEmpty(device.plotIds)

  const plotsQuery = useQuery(client.plots.getMany({ ids: device.plotIds, isArchived: false }), {
    enabled: isLinkedToPlots
  })

  const [linkPlots, linkPlotsRequest] = useMutation(client.devices.linkPlots(device.id))

  const { createPlotOnDevice, mutationState } = useCreatePlotFromDevice({
    device,
    organizationId: org.id,
    onSuccess: (data) => {
      logEvent('plot_creation_irrigation_empty')
      nav(`/farms/${org.id}/plots/${data.id}`)
    },
    onError: (e) => {
      const [nonFieldError] = e.nonFieldErrors ?? []
      let msg: string | undefined = undefined

      if (!isNil(nonFieldError)) {
        const knownErrorCode = t(`error_codes.${nonFieldError.code}`)

        if (!isEmpty(knownErrorCode)) {
          msg = knownErrorCode
        } else {
          msg = nonFieldError.message
        }
      }

      if (!isNil(msg)) addErrorToast(msg)
    }
  })

  const closeAndClear = () => {
    linkPlotsModal.close()
    setSelectedPlots([])
  }

  const linkedPlots = !isNil(plotsQuery.data) ? plotsQuery.data.results : EMPTY_PLOTS

  const renderItem = useCallback(
    (item: Plot) => {
      return (
        <LinkablePlotItem
          key={item.id}
          isSelected={selectedPlots.includes(item.id)}
          onPress={() =>
            setSelectedPlots((current) => {
              let newValue = []

              if (current.includes(item.id)) {
                newValue = current.filter((id) => id !== item.id)
              } else {
                newValue = [...current, item.id]
              }

              return newValue
            })
          }
          plot={item}
          device={device}
        />
      )
    },
    [device, selectedPlots]
  )

  const link = async () => {
    const plotIds = selectedPlots

    await linkPlots(
      { plotIds },
      {
        onSuccess: () => {
          addSuccessToast(t('models.device.successLinkPlot'), {
            action: {
              label: t('models.plot.actions.view_plot', { capitalize: true }),
              onClick: () => {
                nav(`/farms/${org.id}/plots/${plotIds[0]}/weather`)
              }
            }
          })

          closeAndClear()
        },
        // 323: plotNotAllowedToAddThisDeviceOrgRel
        // 603: deviceAlreadyInUse
        // 603_01: deviceMetricsConflictWithDevicePlotConfig
        // 603_04: deviceConfigUpdateMetricsConflict
        // 605: deviceIncompatibleWithPlanOrPlot
        onError: (error: PossibleError) => {
          let message = t('errors.unknown')

          const firstError =
            'nonFieldErrors' in error &&
            !isNil(error.nonFieldErrors) &&
            !isEmpty(error.nonFieldErrors)
              ? error.nonFieldErrors.at(0)
              : undefined

          if (!isNil(firstError?.message) && !isEmpty(firstError.message)) {
            const { message: msg } = firstError
            message = msg
          }

          if (!isNil(firstError?.code)) {
            let fromCode = ''

            if (firstError.code === ErrCode.plotNotAllowedToAddThisDeviceOrgRel) {
              fromCode = t(`error_codes.323`)
            } else if (firstError.code === ErrCode.deviceAlreadyInUse) {
              return t(`error_codes.603`, { capitalize: true })
            } else if (firstError.code === ErrCode.deviceMetricsConflictWithDevicePlotConfig) {
              return t(
                `error_codes.603_01`,
                {
                  parameter_name: device.endUserName.join(' '),
                  plots_list: Object.values(selectedPlots).join(' ?')
                },
                {
                  capitalize: true
                }
              )
            } else if (firstError.code === ErrCode.deviceConfigUpdateMetricsConflict) {
              fromCode = t(`error_codes.603_04`)
            } else if (firstError.code === ErrCode.deviceIncompatibleWithPlanOrPlot) {
              fromCode = t(`error_codes.605`)
            } else {
              fromCode = t(`error_codes.${firstError.code as '501'}`)
            }

            if (!isEmpty(fromCode)) {
              message = fromCode
            }
          }

          addErrorToast(message)
        }
      }
    )
  }

  return (
    <>
      <Flex
        $flexDirection='column'
        $gap='lg'
        $alignItems={'center'}
        $width={'100%'}
        $maxWidth={512}
        $mx={'auto'}
      >
        <Ripple>
          <Icons.Outbound $size='lg' />
        </Ripple>
        <Text $fontWeight='bold' $textAlign='center'>
          {t('models.device.irrigation_data_available_on_plot')}
        </Text>
        {isLinkedToPlots ? (
          <Flex $flexDirection='column' $gap={'md'} $alignSelf='stretch'>
            {linkedPlots.map((plot) => (
              <LinkedPlotLinkItem key={plot.id} plot={plot} device={device} />
            ))}
          </Flex>
        ) : null}
        <Text $textAlign='center'>
          {isLinkedToPlots
            ? t('models.device.can_link_to_another_plot')
            : t('models.device.irrigation_data_available_on_plot_with_escape')}
        </Text>
      </Flex>
      <Flex $flexDirection='column' $gap='lg' $alignItems={'center'}>
        <Button
          importance='sd'
          variant='compact'
          IconLeft={Icons.AddLink}
          onPress={linkPlotsModal.open}
        >
          {t('models.device.actions.link_to_plots', { capitalize: true })}
        </Button>
        <Button
          variant='compact'
          IconLeft={Icons.PlusSignCircle}
          state={mutationState}
          onPress={createPlotOnDevice}
          isDisabled={isNil(device.location)}
        >
          {t('models.plot.actions.create', { capitalize: true })}
        </Button>
      </Flex>
      <Modal
        width={640}
        minHeight={320}
        height={'90%'}
        title={t('models.device.actions.link_to_plots')}
        isOpen={linkPlotsModal.isOpen}
        close={closeAndClear}
        footer={
          <DelimitedFlex $isDelimitedOnTop $p='lg' $justifyContent='flex-end' $gap='md'>
            <Button variant='compact' importance='sd' onPress={closeAndClear}>
              {t('actions.cancel')}
            </Button>
            <Button variant='compact' onPress={link} isLoading={linkPlotsRequest.isPending}>
              {t('actions.confirm')}
            </Button>
          </DelimitedFlex>
        }
      >
        <AdvancedClientList
          inputBgColor={'grayscale.100'}
          searchLabel={t('models.device.actions.select_linked_plots')}
          hideTopNav
          searchable
          renderItem={renderItem}
          ListEmptyComponent={<ListEmpty model='plot' />}
          initialQueryParams={plotListQueryParams}
          requesterBuilder={client.plots.getPage}
        />
      </Modal>
    </>
  )
}

export default IrrigationDeviceDashboardEmptyState
